import React, {useState, useRef, useEffect} from 'react';
import {Box, TextField, Button, Typography, Paper, List, ListItem, ListItemText, Divider, IconButton, CircularProgress, Alert, Snackbar} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {Message} from '../types';
import {sendChat, resetChat} from '../api/chatApi';

export default function ChatPage() {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [snack, setSnack] = useState({open: false, message: '', severity: 'success' as 'success'|'error'});
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || loading) return;

        const userMessage: Message = { role: 'user', content: input.trim() };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const res = await sendChat({ prompt: userMessage.content });
            const assistantMessage: Message = { role: 'assistant', content: res.response };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (err) {
            setSnack({ open: true, message: 'Failed to get response from AI', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async () => {
        if (loading) return;
        if (!window.confirm('Are you sure you want to reset the conversation?')) return;

        try {
            await resetChat();
            setMessages([]);
            setSnack({ open: true, message: 'Conversation reset', severity: 'success' });
        } catch (err) {
            setSnack({ open: true, message: 'Failed to reset conversation', severity: 'error' });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">AI Chat</Typography>
                <Button 
                    variant="outlined" 
                    color="error" 
                    startIcon={<DeleteSweepIcon />} 
                    onClick={handleReset}
                    disabled={loading || messages.length === 0}
                >
                    Reset Conversation
                </Button>
            </Box>

            <Paper sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
                <List>
                    {messages.length === 0 && (
                        <Typography color="textSecondary" align="center" sx={{ mt: 4 }}>
                            No messages yet. Start a conversation!
                        </Typography>
                    )}
                    {messages.map((msg, index) => (
                        <ListItem 
                            key={index} 
                            sx={{ 
                                flexDirection: 'column', 
                                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                mb: 1
                            }}
                        >
                            <Paper 
                                sx={{ 
                                    p: 1.5, 
                                    bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                                    color: msg.role === 'user' ? 'white' : 'text.primary',
                                    maxWidth: '80%',
                                    borderRadius: 2
                                }}
                            >
                                <ListItemText 
                                    primary={msg.content} 
                                    sx={{ 
                                        '& .MuiListItemText-primary': { 
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        } 
                                    }} 
                                />
                            </Paper>
                            <Typography variant="caption" sx={{ mt: 0.5, px: 1 }}>
                                {msg.role === 'user' ? 'You' : 'Assistant'}
                            </Typography>
                        </ListItem>
                    ))}
                    <div ref={messagesEndRef} />
                </List>
            </Paper>

            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    variant="outlined"
                    placeholder="Type your message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                />
                <IconButton 
                    color="primary" 
                    onClick={handleSend} 
                    disabled={loading || !input.trim()}
                    sx={{ alignSelf: 'flex-end', mb: 0.5 }}
                >
                    {loading ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
            </Box>

            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({...s, open: false}))}>
                <Alert severity={snack.severity} variant="filled">
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
